import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, ExternalLink, Mail, Copy, Check, Users, BookOpen, MessageCircle, AlertCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SKOOL_LINK = 'https://www.skool.com/hustler-digital-academy-6892/about?ref=384dcfd02a1b4883b79e86c4db2c2445';
const AFFILIATE_EMAIL = 'adhesion.hda@gmail.com';

export const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      // No session ID but user landed here - show success anyway (might be direct link)
      setPaymentStatus('success');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
        const data = await response.json();

        if (data.payment_status === 'paid' || data.status === 'complete') {
          setPaymentStatus('success');
        } else if (data.status === 'expired') {
          setPaymentStatus('expired');
        } else {
          // If session exists, show success (Stripe redirects only on success)
          setPaymentStatus('success');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        // On error, still show success page since Stripe only redirects on successful payment
        setPaymentStatus('success');
      }
    };

    // Short delay then check
    const timer = setTimeout(checkPaymentStatus, 1500);
    return () => clearTimeout(timer);
  }, [sessionId]);

  const copyEmail = () => {
    navigator.clipboard.writeText(AFFILIATE_EMAIL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (paymentStatus === 'checking') {
    return (
      <div data-testid="success-page-loading" className="min-h-screen bg-[#0D1F17] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#F5EFE3] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-heading text-white uppercase mb-2">Vérification du paiement...</h2>
          <p className="text-white/60">Merci de patienter quelques instants</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'expired') {
    return (
      <div data-testid="success-page-error" className="min-h-screen bg-[#0D1F17] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-heading text-white uppercase mb-2">Session expirée</h2>
          <p className="text-white/60 mb-6">
            La session de paiement a expiré. Veuillez réessayer.
          </p>
          <Link 
            to="/" 
            className="btn-primary inline-block"
          >
            Retour à la page principale
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="success-page" className="min-h-screen bg-[#0D1F17] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-[#F5EFE3]/20 flex items-center justify-center mx-auto mb-6 neon-glow">
            <CheckCircle className="w-10 h-10 text-[#F5EFE3]" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase text-white mb-4">
            Félicitations ! <span className="text-[#F5EFE3]">Bienvenue</span>
          </h1>
          <p className="text-white/60 text-lg">
            Tu fais maintenant partie de la Hustler Digital Academy
          </p>
        </div>

        {/* Important Alert */}
        <div className="bg-[#F5EFE3]/10 border-2 border-[#F5EFE3]/50 rounded-lg p-6 mb-8 text-center">
          <p className="text-[#F5EFE3] text-lg md:text-xl font-bold mb-2">
            IMPORTANT : Suis les 3 étapes ci-dessous pour accéder à ta formation
          </p>
          <p className="text-white/70 text-sm">
            Garde cette page ouverte ou fais une capture d'écran
          </p>
        </div>

        {/* Instructions Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Step 1: Join Formation */}
          <div className="bg-[#151A18] border border-white/10 rounded-lg p-6 relative group hover:border-[#F5EFE3]/30 transition-colors">
            <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#F5EFE3] flex items-center justify-center text-black font-bold text-lg">
              1
            </div>
            <BookOpen className="w-10 h-10 text-[#F5EFE3] mb-4 mt-2" />
            <h3 className="text-lg font-bold text-white mb-2">Rejoindre la formation</h3>
            <p className="text-white/60 text-sm mb-4">
              Clique sur le bouton et inscris-toi avec ton Nom, Prénom et Email pour accéder aux modules.
            </p>
            <a 
              href={SKOOL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="skool-link-button"
              className="btn-primary w-full text-center text-sm py-3 inline-flex items-center justify-center gap-2"
            >
              Accéder à la formation
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Step 2: Get Affiliate Link */}
          <div className="bg-[#151A18] border border-white/10 rounded-lg p-6 relative group hover:border-[#F5EFE3]/30 transition-colors">
            <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#F5EFE3] flex items-center justify-center text-black font-bold text-lg">
              2
            </div>
            <Mail className="w-10 h-10 text-[#F5EFE3] mb-4 mt-2" />
            <h3 className="text-lg font-bold text-white mb-2">Obtenir ton lien affilié</h3>
            <p className="text-white/60 text-sm mb-4">
              Envoie un email pour recevoir ton lien et commencer à gagner 80% de commission.
            </p>
            <div className="bg-[#151A18] border border-white/10 rounded px-3 py-2 mb-3">
              <p className="text-white/80 text-sm font-mono truncate">{AFFILIATE_EMAIL}</p>
            </div>
            <button 
              onClick={copyEmail}
              data-testid="copy-email-button"
              className="btn-secondary w-full text-sm py-3 inline-flex items-center justify-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copié !
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copier l'email
                </>
              )}
            </button>
          </div>

          {/* Step 3: Tips */}
          <div className="bg-[#151A18] border border-white/10 rounded-lg p-6 relative group hover:border-[#F5EFE3]/30 transition-colors">
            <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#F5EFE3] flex items-center justify-center text-black font-bold text-lg">
              3
            </div>
            <MessageCircle className="w-10 h-10 text-[#F5EFE3] mb-4 mt-2" />
            <h3 className="text-lg font-bold text-white mb-2">Conseils importants</h3>
            <ul className="text-white/60 text-sm space-y-3">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#F5EFE3] flex-shrink-0 mt-0.5" />
                <span>Utilise le <strong>même email</strong> sur Skool que pour l'affiliation</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#F5EFE3] flex-shrink-0 mt-0.5" />
                <span>Ton lien affilié = <strong>80% commission</strong> par vente</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#F5EFE3] flex-shrink-0 mt-0.5" />
                <span>Rejoins le groupe privé pour poser tes questions</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Commission highlight */}
        <div className="bg-gradient-to-r from-[#F5EFE3]/10 via-[#F5EFE3]/5 to-[#F5EFE3]/10 border border-[#F5EFE3]/30 rounded-lg p-8 text-center mb-12">
          <p className="text-white/60 uppercase tracking-wider text-sm mb-2">Ta commission par vente</p>
          <div className="text-[#F5EFE3] text-6xl md:text-7xl font-bold font-heading">
            80%
          </div>
          <p className="text-white mt-2">soit <span className="text-[#F5EFE3] font-bold">157,60€</span> dans ta poche à chaque vente</p>
        </div>

        {/* Group reminder */}
        <div className="bg-[#151A18] border border-white/10 rounded-lg p-6 text-center">
          <Users className="w-8 h-8 text-[#F5EFE3] mx-auto mb-3" />
          <h3 className="text-lg font-bold text-white mb-2">Rejoins le groupe privé</h3>
          <p className="text-white/60 text-sm">
            Une fois inscrit sur Skool, tu auras accès au groupe privé avec la communauté, 
            les coachings et les appels de groupe. C'est là que tout se passe !
          </p>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-white/40 text-sm">
            Des questions ? Contacte-nous sur Instagram <a href="https://instagram.com/samba_bigsam" target="_blank" rel="noopener noreferrer" className="text-[#F5EFE3] hover:underline">@samba_bigsam</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
